import { useEffect, useRef } from "react";

export function MorphingBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseXRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const handleMouseMove = (e: MouseEvent) => {
      mouseXRef.current = e.clientX;
    };
    const handleMouseLeave = () => {
      mouseXRef.current = null;
    };
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleMouseLeave);

    // Shape functions
    const shapes = [
      (t: number) => Math.sin(t),
      (t: number) => (2 / Math.PI) * Math.asin(Math.sin(t)),
      (t: number) => {
        const p = (((t / (2 * Math.PI)) % 1) + 1) % 1;
        return 2 * p - 1;
      },
      (t: number) =>
        Math.sin(t) * 0.6 + Math.sin(t * 3) * 0.3 + Math.sin(t * 5) * 0.1,
    ];

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const centerY = h / 2;
      const mouseX = mouseXRef.current;
      const isDark = document.documentElement.classList.contains("dark");

      ctx.clearRect(0, 0, w, h);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // Morph between shapes
      const cycleDuration = 30;
      const cyclePos = (time * 0.5) % cycleDuration;
      const shapeIndex = Math.floor((cyclePos / cycleDuration) * 4);
      const morphFactor = ((cyclePos / cycleDuration) * 4) % 1;
      const shapeA = shapes[shapeIndex % shapes.length]!;
      const shapeB = shapes[(shapeIndex + 1) % shapes.length]!;
      const smooth = morphFactor * morphFactor * (3 - 2 * morphFactor);

      // Two wave layers with slight offset for richness
      const waves = [
        { freqMul: 1.0, ampMul: 1.0, phase: 0, yOff: 0 },
        { freqMul: 1.08, ampMul: 0.85, phase: 0.8, yOff: 10 },
      ];

      for (const wave of waves) {
        const frequency = (2 + Math.sin(time * 0.15) * 0.3) * wave.freqMul;
        const amplitude =
          (h * 0.15 + Math.sin(time * 0.1) * h * 0.03) * wave.ampMul;

        // Compute points for this wave
        const points: [number, number][] = [];
        for (let x = 0; x <= w; x += 2) {
          const nx = x / w;
          const t =
            nx * Math.PI * 2 * frequency + time * 0.4 + wave.phase;

          const valA = shapeA(t);
          const valB = shapeB(t);
          let y =
            centerY +
            wave.yOff +
            (valA * (1 - smooth) + valB * smooth) * amplitude;

          if (mouseX !== null) {
            const dist = Math.abs(nx - mouseX / w);
            if (dist < 0.15) {
              const influence = (1 - dist / 0.15) * 12;
              y += Math.sin(nx * 20 + time * 3) * influence;
            }
          }

          points.push([x, y]);
        }

        const tracePath = () => {
          for (let i = 0; i < points.length; i++) {
            const [px, py] = points[i]!;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
        };

        // Multi-pass bloom: wide faint → narrow bright
        // Each pass gets thinner and more opaque, creating a smooth glow
        if (isDark) {
          // Dark theme: purple/violet outer glow → white hot core
          const passes = [
            { w: 40, color: "rgba(139, 92, 246, 0.015)" },
            { w: 24, color: "rgba(139, 92, 246, 0.03)" },
            { w: 14, color: "rgba(167, 139, 250, 0.05)" },
            { w: 8, color: "rgba(196, 181, 253, 0.07)" },
            { w: 4, color: "rgba(221, 214, 254, 0.12)" },
            { w: 2, color: "rgba(245, 243, 255, 0.2)" },
            { w: 1, color: "rgba(255, 255, 255, 0.35)" },
          ];
          for (const p of passes) {
            ctx.beginPath();
            ctx.strokeStyle = p.color;
            ctx.lineWidth = p.w;
            tracePath();
            ctx.stroke();
          }
        } else {
          // Light theme: soft purple outer → dark core
          const passes = [
            { w: 36, color: "rgba(139, 92, 246, 0.02)" },
            { w: 20, color: "rgba(139, 92, 246, 0.03)" },
            { w: 10, color: "rgba(100, 60, 180, 0.05)" },
            { w: 5, color: "rgba(60, 40, 120, 0.08)" },
            { w: 2, color: "rgba(40, 30, 80, 0.14)" },
            { w: 1, color: "rgba(25, 20, 50, 0.22)" },
          ];
          for (const p of passes) {
            ctx.beginPath();
            ctx.strokeStyle = p.color;
            ctx.lineWidth = p.w;
            tracePath();
            ctx.stroke();
          }
        }
      }

      time += 0.008;
      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0"
      style={{ zIndex: 0 }}
    />
  );
}
